import React from 'react'
import { useTheme } from '../../../components/hooks/useTheme';
import { Divider, FormControlLabel, Radio, RadioGroup } from '@mui/material';

type ReservationSettingsProps = {
    language: string;
    setLanguage: (value: string) => void;
    renewalDisabled: boolean;
    setRenewalDisabled: (value: boolean) => void;
    renewContractsXDaysInAdvance: number;
    setRenewContractsXDaysInAdvance: (value: number) => void;
    forceRenewalToXDays: number;
    setForceRenewalToXDays: (value: number) => void;
    renewContractXHoursAfterPlannedReturnTime: number;
    setRenewContractXHoursAfterPlannedReturnTime: (value: number) => void;
    bookingHappendAfterSaleEmailSent: boolean;
    setBookingHappendAfterSaleEmailSent: (value: boolean) => void;
    isThisWalkInCustomer: boolean;
    setIsThisWalkInCustomer: (value: boolean) => void;
}

const ReservationSettings = ({
    language,
    setLanguage,
    renewalDisabled,
    setRenewalDisabled,
    renewContractsXDaysInAdvance,
    setRenewContractsXDaysInAdvance,
    forceRenewalToXDays,
    setForceRenewalToXDays,
    renewContractXHoursAfterPlannedReturnTime,
    setRenewContractXHoursAfterPlannedReturnTime,
    bookingHappendAfterSaleEmailSent,
    setBookingHappendAfterSaleEmailSent,
    isThisWalkInCustomer,
    setIsThisWalkInCustomer
}: ReservationSettingsProps) => {
    const { themeStyles } = useTheme();

    return (
        <div className="mt-[1.5rem]">
            <span className={`text-[14px] font-semibold inline-block mb-2 ${themeStyles.main.textHeadingColor}`}>
                Reservation Settings
            </span>

            <Divider className={`w-full ${themeStyles.divider.bg}`} />

            <div className="grid grid-cols-2 gap-x-6 gap-y-2 mt-[1rem]">
                <div>
                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Language</label>
                    <select
                        className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                        value={language}
                        onChange={(e) => setLanguage(e.target.value)}
                    >
                        <option value={"English"}>English</option>
                    </select>
                </div>

                <div>
                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Renewal Disabled</label>
                    <RadioGroup
                        row
                        value={renewalDisabled ? "Yes" : "No"}
                        onChange={(e) => setRenewalDisabled(e.target.value === "Yes")}
                    >
                        <FormControlLabel
                            value="Yes"
                            control={
                                <Radio
                                    sx={{
                                        color: "gray",
                                        "&.Mui-checked": {
                                            color: "#167AC3",
                                        },
                                        '& .MuiSvgIcon-root': {
                                            fontSize: 14,
                                        },
                                    }}
                                />
                            }
                            label="Yes"
                            sx={{
                                color: themeStyles.form.labelTextColor
                            }}
                        />

                        <FormControlLabel
                            value="No"
                            control={
                                <Radio
                                    sx={{
                                        color: "gray",
                                        "&.Mui-checked": {
                                            color: "#167AC3",
                                        },
                                        '& .MuiSvgIcon-root': {
                                            fontSize: 14,
                                        },
                                    }}
                                />
                            }
                            label="No"
                            sx={{
                                color: themeStyles.form.labelTextColor
                            }}
                        />
                    </RadioGroup>
                </div>

                <div>
                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Renew Contracts XDays In Advance</label>
                    <input
                        type="text"
                        inputMode="numeric"
                        className={`w-full px-[8px] py-[6px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                        value={renewContractsXDaysInAdvance}
                        onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "");
                            setRenewContractsXDaysInAdvance(value === "" ? 0 : Number(value));
                        }}
                        onKeyDown={(e) => {
                            const allowedKeys = [
                                "Backspace",
                                "Delete",
                                "ArrowLeft",
                                "ArrowRight",
                                "Tab",
                                "Home",
                                "End",
                            ];

                            if (!allowedKeys.includes(e.key) && !/^\d$/.test(e.key)) {
                                e.preventDefault();
                            }
                        }}
                        onPaste={(e) => {
                            const pasted = e.clipboardData.getData("text");

                            if (!/^\d+$/.test(pasted)) {
                                e.preventDefault();
                            }
                        }}
                        onDrop={(e) => e.preventDefault()}
                        autoComplete="off"
                    />
                </div>

                <div>
                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Force Renewal To XDays</label>
                    <input
                        type="text"
                        inputMode="numeric"
                        className={`w-full px-[8px] py-[6px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                        value={forceRenewalToXDays}
                        onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "");
                            setForceRenewalToXDays(value === "" ? 0 : Number(value));
                        }}
                        onKeyDown={(e) => {
                            const allowedKeys = [
                                "Backspace",
                                "Delete",
                                "ArrowLeft",
                                "ArrowRight",
                                "Tab",
                                "Home",
                                "End",
                            ];

                            if (!allowedKeys.includes(e.key) && !/^\d$/.test(e.key)) {
                                e.preventDefault();
                            }
                        }}
                        onPaste={(e) => {
                            const pasted = e.clipboardData.getData("text");

                            if (!/^\d+$/.test(pasted)) {
                                e.preventDefault();
                            }
                        }}
                        onDrop={(e) => e.preventDefault()}
                        autoComplete="off"
                    />
                </div>

                <div>
                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Renew Contract XHours After Planned ReturnTime</label>
                    <input
                        type="text"
                        inputMode="numeric"
                        className={`w-full px-[8px] py-[6px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                        value={renewContractXHoursAfterPlannedReturnTime}
                        onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "");
                            setRenewContractXHoursAfterPlannedReturnTime(value === "" ? 0 : Number(value));
                        }}
                        onKeyDown={(e) => {
                            const allowedKeys = [
                                "Backspace",
                                "Delete",
                                "ArrowLeft",
                                "ArrowRight",
                                "Tab",
                                "Home",
                                "End",
                            ];

                            if (!allowedKeys.includes(e.key) && !/^\d$/.test(e.key)) {
                                e.preventDefault();
                            }
                        }}
                        onPaste={(e) => {
                            const pasted = e.clipboardData.getData("text");

                            if (!/^\d+$/.test(pasted)) {
                                e.preventDefault();
                            }
                        }}
                        onDrop={(e) => e.preventDefault()}
                        autoComplete="off"
                    />
                </div>

                <div />

                <div>
                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Booking Happend After SaleEmail Sent</label>
                    <RadioGroup
                        row
                        value={bookingHappendAfterSaleEmailSent ? "Yes" : "No"}
                        onChange={(e) => setBookingHappendAfterSaleEmailSent(e.target.value === "Yes")}
                    >
                        <FormControlLabel
                            value="Yes"
                            control={
                                <Radio
                                    sx={{
                                        color: "gray",
                                        "&.Mui-checked": {
                                            color: "#167AC3",
                                        },
                                        '& .MuiSvgIcon-root': {
                                            fontSize: 14,
                                        },
                                    }}
                                />
                            }
                            label="Yes"
                            sx={{
                                color: themeStyles.form.labelTextColor
                            }}
                        />

                        <FormControlLabel
                            value="No"
                            control={
                                <Radio
                                    sx={{
                                        color: "gray",
                                        "&.Mui-checked": {
                                            color: "#167AC3",
                                        },
                                        '& .MuiSvgIcon-root': {
                                            fontSize: 14,
                                        },
                                    }}
                                />
                            }
                            label="No"
                            sx={{
                                color: themeStyles.form.labelTextColor
                            }}
                        />
                    </RadioGroup>
                </div>

                <div>
                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Walk in Customer?</label>
                    <RadioGroup
                        row
                        value={isThisWalkInCustomer ? "Yes" : "No"}
                        onChange={(e) => setIsThisWalkInCustomer(e.target.value === "Yes")}
                    >
                        <FormControlLabel
                            value="Yes"
                            control={
                                <Radio
                                    sx={{
                                        color: "gray",
                                        "&.Mui-checked": {
                                            color: "#167AC3",
                                        },
                                        '& .MuiSvgIcon-root': {
                                            fontSize: 14,
                                        },
                                    }}
                                />
                            }
                            label="Yes"
                            sx={{
                                color: themeStyles.form.labelTextColor
                            }}
                        />

                        <FormControlLabel
                            value="No"
                            control={
                                <Radio
                                    sx={{
                                        color: "gray",
                                        "&.Mui-checked": {
                                            color: "#167AC3",
                                        },
                                        '& .MuiSvgIcon-root': {
                                            fontSize: 14,
                                        },
                                    }}
                                />
                            }
                            label="No"
                            sx={{
                                color: themeStyles.form.labelTextColor
                            }}
                        />
                    </RadioGroup>
                </div>
            </div>
        </div>
    )
}

export default ReservationSettings